/**
 * RetentionGeoFilter - селектор гео для раздела «Ретеншен»
 * ==========================================================
 *
 * Мультивыбор стран: чекбоксы в поповере, поиск по коду, размер когорты рядом
 * с каждой страной (чтобы было видно, где выборка вообще есть). Пустой выбор =
 * все страны.
 *
 * Список стран тянется ЛЕНИВО, при первом открытии поповера: запрос идёт по той
 * же зоне таблицы событий, что и сам ретеншен, и дёргать его на каждой загрузке
 * раздела незачем.
 */

import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Globe, X } from 'lucide-react';
import { useFilters } from '../../hooks/useFilters';
import { MetricsService } from '../../services/metricsService';

const fmtInt = (value) => Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

function triggerLabel(geos) {
  if (geos.length === 0) return 'Все страны';
  if (geos.length <= 3) return geos.join(', ');
  return `${geos.slice(0, 2).join(', ')} +${geos.length - 2}`;
}

export function RetentionGeoFilter({ geos, onToggleGeo, onClearGeos, maxGeos }) {
  const { filters } = useFilters();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Грузим при КАЖДОМ открытии, а не однажды: иначе после смены дат или проекта
  // в селекторе остались бы страны и размеры когорт от прежнего окна — по ним
  // как раз и решают, где выборка достаточна. Повторный запрос почти всегда
  // отдаётся из клиентского кэша (MetricsService.cachedGet), поэтому дешёвый.
  const loadOptions = async () => {
    if (loading) return;

    setLoading(true);
    setError(null);
    try {
      const rows = await MetricsService.getRetentionGeoOptions(filters);
      setOptions(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить список стран');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) loadOptions();
  };

  // Выбранные страны показываем всегда, даже если их нет в загруженном списке
  // (окно дат могло измениться уже после загрузки списка).
  const visibleOptions = useMemo(() => {
    const rows = options || [];
    const known = new Set(rows.map((row) => row.geo));
    const orphans = geos
      .filter((geo) => !known.has(geo))
      .map((geo) => ({ geo, cohortSize: null }));

    const query = search.trim().toUpperCase();
    return [...orphans, ...rows].filter((row) => !query || row.geo.startsWith(query));
  }, [options, geos, search]);

  const atLimit = maxGeos !== undefined && geos.length >= maxGeos;

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
            <Globe className="h-3 w-3" />
            {triggerLabel(geos)}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск: US, DE, ..."
            className="h-8 text-xs mb-2"
          />

          {loading && <p className="text-xs text-muted-foreground px-1 py-2">Загрузка стран…</p>}
          {error && <p className="text-xs text-destructive px-1 py-2">{error}</p>}
          {atLimit && (
            <p className="text-xs text-muted-foreground px-1 pb-2">Выбрано максимум стран: {maxGeos}</p>
          )}
          {!loading && !error && visibleOptions.length === 0 && (
            <p className="text-xs text-muted-foreground px-1 py-2">Страны не найдены</p>
          )}

          <div className="max-h-64 overflow-y-auto space-y-1">
            {visibleOptions.map((row) => (
              <label
                key={row.geo}
                className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted/50 cursor-pointer text-xs"
              >
                <Checkbox
                  checked={geos.includes(row.geo)}
                  disabled={atLimit && !geos.includes(row.geo)}
                  onCheckedChange={() => onToggleGeo(row.geo)}
                />
                <span className="font-medium">{row.geo}</span>
                <span className="ml-auto text-muted-foreground">
                  {row.cohortSize === null ? '—' : fmtInt(row.cohortSize)}
                </span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {geos.length > 0 && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClearGeos}
          title="Сбросить гео"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export default RetentionGeoFilter;
